import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeReadOnlyField — Read-only display field with copy button.
 * Used for showing generated URLs, IDs, tokens, etc.
 *
 * Usage:
 *   <NodeReadOnlyField
 *     label="Webhook URL"
 *     value="https://api.workflow86.com/webhook/abc123"
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const FieldContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: NODE_UI.spacing.sm,
  padding: '6px 8px',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.04)'
    : '#f8f8f8',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadiusSm,
  minHeight: '32px',
}));

const ValueText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.primary,
  fontFamily: 'monospace',
  wordBreak: 'break-all',
  flex: 1,
  lineHeight: 1.4,
  userSelect: 'all',
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const NodeReadOnlyField = React.memo(function NodeReadOnlyField({
  label,
  value = '',
  showCopy = true,
  monospace = true,
  sx,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e) => {
    stopPropagation(e);
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS contexts
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [value]);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <FieldContainer sx={sx}>
        <ValueText sx={monospace ? undefined : { fontFamily: 'inherit' }}>
          {value}
        </ValueText>
        {showCopy && value && (
          <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
            <CopyButton size="small" onClick={handleCopy} onMouseDown={stopPropagation}>
              {copied ? (
                <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </CopyButton>
          </Tooltip>
        )}
      </FieldContainer>
    </div>
  );
});

export default NodeReadOnlyField;
